import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Edit2,
  Trash2,
  GitBranch,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Pipeline } from "@/types/pipeline";

interface PipelinesTableProps {
  pipelines: Pipeline[];
  onAssignPermissions: (pipeline: Pipeline) => void;
  onDelete: (pipelineId: string) => void;
  onViewDetails: (pipeline: Pipeline) => void;
}

export function PipelinesTable({
  pipelines,
  onAssignPermissions,
  onDelete,
  onViewDetails,
}: PipelinesTableProps) {
  const handleDelete = (pipeline: Pipeline) => {
    onDelete(pipeline.id);
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Pipeline
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Meta de Ventas
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {pipelines.map((pipeline) => (
                <tr key={pipeline.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <GitBranch className="h-5 w-5 text-blue-600 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-slate-900">
                          {pipeline.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-slate-600 max-w-xs truncate">
                      {pipeline.description || "-"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge
                      className={
                        pipeline.is_default
                          ? "bg-blue-100 text-blue-800"
                          : "bg-slate-100 text-slate-800"
                      }
                    >
                      {pipeline.is_default ? "Por defecto" : "Personalizado"}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-slate-800 font-medium">
                      {(pipeline.income_objective || 0).toLocaleString(
                        "es-ES",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewDetails(pipeline)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        aria-label={`Editar ${pipeline.name}`}
                        title="Editar pipeline"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(pipeline)}
                        disabled={pipeline.is_default}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={`Eliminar ${pipeline.name}`}
                        title={
                          pipeline.is_default
                            ? "No se puede eliminar el pipeline por defecto"
                            : "Eliminar pipeline"
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
